import React, { useEffect, useLayoutEffect, useContext, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import gsap from 'gsap';

import TrajectSelectModal from './../components/TrajectSelectModal';

import setFullscreenVideoAspectRatio from '../helpers/VideoHelper';
import SideYearNav from '../components/SideYearNav';
import { MainContext } from '../components/MainLayoutWrapper';
import VakcurListCard from '../components/VakcurListCard';

const bgVideos = require.context('../assets/videos/vaktak', true);
const vakVideoMap = {};
bgVideos.keys().map(key => vakVideoMap[key.replace('./', '').replace('.mp4', '')] = bgVideos(key));

// TODO: We need to combine the free spots for each vak (0,1,2) with the videos above

function PageVak() {
    const navigate = useNavigate();
    const params = useParams();
    const mainContext = useContext(MainContext);

    const [showAltImages, setShowAltImages] = useState(false);
    const [videoUrl, setVideoUrl] = useState(null);
    const videoEl = useRef(null);
    const altImgWrapper = useRef(null);

    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showTrajectSelect, setShowTrajectSelect] = useState(false);

    const vakSlug = params.vak || null;
    const currentVakcurVak = mainContext.vakken.find(vak => vak.slug === vakSlug);

    const currentVakcurKlas = mainContext.klassen.find(klas => parseInt(klas.slug) === parseInt(mainContext.activeFilters.klas));
    const currentCardsFiltered = getCurrentCardsArrayFiltered();

    function resize() {
        if (videoEl.current !== null) setFullscreenVideoAspectRatio(videoEl.current, 16 / 9);
        if (altImgWrapper.current !== null) setFullscreenVideoAspectRatio(altImgWrapper.current, 16 / 9);
    }

    resize();

    function trajectSelected(trajectSlug) {
        if (trajectSlug !== null) {
            mainContext.helperFunctions.setTrajectFilter(trajectSlug);
        }
        else {
            setShowTrajectSelect(false);
            navigate('/vakcurriculum');
        }
    }

    function getCurrentCardsArrayFiltered() {
        if (mainContext.activeFilters.vak === null) return [];

        const cards = [];
        const klas = mainContext.klassen.find(klas => parseInt(klas.slug) === parseInt(mainContext.activeFilters.klas));

        if (klas) cards.push(...Object.values(klas.kaarten));
        else mainContext.klassen.map(klas => cards.push(...Object.values(klas.kaarten)));

        return cards.filter(card => {
            if (!card.vakken.includes(vakSlug)) return false;
            if ((!!klas && !!klas.trajecten.length) && mainContext.activeFilters.traject !== null && !card.trajecten.includes(mainContext.activeFilters.traject)) return false;
            if (mainContext.activeFilters.lestype !== null && !card.lestypen.includes(mainContext.activeFilters.lestype)) return false;

            if (mainContext.activeFilters.tijdvak !== null && !card.tijdvakken.includes(mainContext.activeFilters.tijdvak)) return false;
            if (mainContext.activeFilters.vakoverstijging !== null && !card.vakoverstijgingen.includes(mainContext.activeFilters.vakoverstijging)) return false;
            if (mainContext.activeFilters.huiswerk !== null && !card.huiswerk.includes(mainContext.activeFilters.huiswerk)) return false;
            if (mainContext.activeFilters.leerfocus !== null && !card.leerfocus.includes(mainContext.activeFilters.leerfocus)) return false;
            if (mainContext.activeFilters.afsluiting !== null && !card.afsluitingen.includes(mainContext.activeFilters.afsluiting)) return false;
            if (mainContext.activeFilters.pta !== null && !card.pta.includes(mainContext.activeFilters.pta)) return false;
            if (mainContext.activeFilters.portfolio !== null && !card.portfolio.includes(mainContext.activeFilters.portfolio)) return false;

            return true;
        });
    }

    function goToCard(slug) {
        navigate(`/vakcurriculum/kaart/${slug}`);
    }

    function getCardTitle(card) {
        if (card.thema) return card.thema;
        if (card.tijdvakken_format.length) {
            if (!mainContext.activeFilters.klas && !!card.klassen.length) return `Klas ${card.klassen.join('/')} tijdvak ${card.tijdvakken_format.join(', ')}`;
            return `Tijdvak ${card.tijdvakken_format.join(', ')}`;
        }
        if (card.vakken_format.length) return card.vakken_format.join(', ');

        return card.name;
    }

    // If we visit this page but active filter is not, set active filter to vakSlug
    // useEffect(() => {
        // if (vakSlug !== null && mainContext.activeFilters.vak === null) {
            // mainContext.helperFunctions.setVakFilter(vakSlug);
        // }
    // }, [vakSlug, mainContext.activeFilters.vak, mainContext.helperFunctions]);

    useEffect(() => {
        if (vakSlug === null) return null;

        if (vakVideoMap.hasOwnProperty(vakSlug)) return setVideoUrl(vakVideoMap[vakSlug]);
        return setVideoUrl(vakVideoMap['onbekend']);
    }, [vakSlug]);

    useEffect(() => {
        if (mainContext.activeFilters.vak !== null) {
            const currentKlas = mainContext.klassen.find(klas => parseInt(klas.slug) === parseInt(mainContext.activeFilters.klas));
            setShowTrajectSelect((!!currentKlas && Object.values(currentKlas.trajecten).length > 0) && mainContext.activeFilters.traject === null);
        }
    }, [mainContext.activeFilters.klas, mainContext.activeFilters.vak, mainContext.activeFilters.klassen, mainContext.activeFilters.traject, mainContext.klassen, mainContext.vakken]);

    useEffect(() => {
        if (mainContext.klassen.length > 0 && videoEl.current !== null) {
            gsap.set(altImgWrapper.current, { opacity: 0 });
            videoEl.current.play();
            setTimeout(() => gsap.to(altImgWrapper.current, { duration: 0.5, opacity: 1, ease: 'power2.inOut', onComplete: () => setShowAltImages(true) }), 1700);
        }
    }, [mainContext.klassen, videoUrl]);

    // useEffect(() => {
    //     if (mainContext.klassen.length > 0 && videoEl.current !== null) {
    //         videoEl.current.play();
    //     }
    // }, [vakSlug]);

    useLayoutEffect(() => {
        if (videoEl.current === null) return;

        resize();
        window.addEventListener('resize', resize);

        const interval = setInterval(() => {
            if (videoEl.current.currentTime > 6) {
                videoEl.current.currentTime = 2;
            }
        }, 100);

        return () => {
            clearInterval(interval);
            window.removeEventListener('resize', resize);
        };
    });

    return (
        <div className="page-vak">
            {currentCardsFiltered.length === 0 && <div className="page-vak__no-cards-msg">Er zijn geen resultaten gevonden voor de huidige filtercombinatie.</div>}

            {!!currentVakcurKlas ? <>
                <SideYearNav showInfoModal={showInfoModal} setShowInfoModal={setShowInfoModal} />

                <ul className="page-vak__cards">
                    {currentCardsFiltered.map((card, index) => {
                        const style = {};
                        if (!!card.featured_image.thumbnail) style.backgroundImage = `url(${card.featured_image.thumbnail})`;

                        return (
                            <li key={`page-vak-card-${index}`} className={'page-vak__card' + (!card.featured_image.thumbnail ? ' page-vak__card--no-image' : '')}>
                                <button type="button" className="page-vak__card__btn" onClick={() => goToCard(card.slug)}>
                                    <div className="page-vak__card__img-wrapper"><div className="page-vak__card__img" style={style}></div></div>
                                    <span className="page-vak__card__title">{getCardTitle(card)}</span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </> :
                <ul className="page-vak__card-grid">
                    {currentCardsFiltered.map((card, index) => {
                        return (<VakcurListCard key={`page-vak-grid-card-${index}`} card={card} showThumbnail={false} />);
                    })}
                </ul>
            }

            {mainContext.klassen.length === 0 && <div className="loading-msg">Laden..</div>}

            {!!currentVakcurVak && <TrajectSelectModal show={showTrajectSelect} setShow={setShowTrajectSelect} options={Object.values(currentVakcurVak.trajecten).map(traject => { return { title: traject.name, slug: traject.slug } })} callback={trajectSelected} />}

            <div className={'page-vak__bg-wrapper' + (currentVakcurKlas ? ' page-vak__bg-wrapper--overlay' : '')}>
                <video ref={videoEl} src={videoUrl} type="video/mp4" playsInline muted></video>

                <div ref={altImgWrapper} className={'page-vak__bg-wrapper__alt-images' + (showAltImages ? ' page-vak__bg-wrapper__alt-images--sow' : '')}>
                    {(!!currentVakcurVak && typeof currentVakcurVak.hasOwnProperty['alt_images'] && currentVakcurVak.alt_images.length > 0) && currentVakcurVak.alt_images.map((image, index) => {
                        return <div key={`page-vak-alt-image-${index}`} className="page-vak__bg-wrapper__alt-image" style={{ backgroundImage: `url(${image.thumbnail})` }}></div>;
                    })}
                </div>
            </div>
        </div>
    );
}

export default PageVak;